import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Images, Search, ChevronRight, Film, Link2, Image as ImageIcon, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url}`;
};

const MediaLanding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [recent, setRecent] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!['Admin', 'Staff'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    (async () => {
      try {
        const [athletesRes, recentRes, featuredRes] = await Promise.all([
          axios.get(`${API}/athletes`),
          axios.get(`${API}/media/recent?limit=16`).catch(() => ({ data: [] })),
          axios.get(`${API}/media/featured`).catch(() => ({ data: [] })),
        ]);
        setAthletes(athletesRes.data || []);
        setRecent(Array.isArray(recentRes.data) ? recentRes.data : []);
        setFeatured(Array.isArray(featuredRes.data) ? featuredRes.data : []);
      } catch {
        toast.error('Failed to load media');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const filtered = athletes.filter(a =>
    !q || (a.full_name || '').toLowerCase().includes(q.toLowerCase()) || (a.squad || '').toLowerCase().includes(q.toLowerCase())
  );

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return ''; }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="media-landing-page">
        <div className="mb-5">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Media & Highlights
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">Latest uploads across all athletes — pick an athlete below to browse their full gallery.</p>
        </div>

        {/* ====== FEATURED SHOWCASE ====== */}
        {featured.length > 0 && (
          <div className="mb-8" data-testid="featured-section">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                Featured Highlights
              </h3>
              <span className="text-xs text-gray-400">{featured.length} curated</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="featured-grid">
              {featured.map(item => {
                const thumb = absoluteUrl(item.thumbnail_url);
                const isHighlight = item.item_type === 'highlight';
                const isLink = item.media_type === 'link';
                return (
                  <div
                    key={`f-${item.item_type}-${item.id}`}
                    onClick={() => navigate(`/athletes/${item.athlete_id}/media`)}
                    className="group cursor-pointer rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 overflow-hidden hover:shadow-lg hover:border-amber-400 transition-all"
                    data-testid={`featured-item-${item.id}`}
                  >
                    <div className="relative aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      {thumb ? (
                        <img
                          src={thumb} alt={item.caption || 'media'} loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className={`absolute inset-0 ${thumb ? 'hidden' : 'flex'} items-center justify-center text-gray-400`}>
                        {isHighlight ? <Film className="w-7 h-7" /> : isLink ? <Link2 className="w-7 h-7" /> : <ImageIcon className="w-7 h-7" />}
                      </div>
                      <div className="absolute top-1.5 left-1.5"><Star className="w-4 h-4 fill-amber-400 text-amber-500 drop-shadow" /></div>
                      <div className="absolute top-1.5 right-1.5">
                        <Badge className={`text-[10px] px-1.5 py-0 ${isHighlight ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                          {item.label}
                        </Badge>
                      </div>
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate" title={item.athlete_name}>{item.athlete_name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{item.caption || '—'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ====== RECENT UPLOADS ====== */}
        <div className="mb-8" data-testid="recent-uploads-section">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">Recent Uploads</h3>
            {recent.length > 0 && <span className="text-xs text-gray-400">{recent.length} items</span>}
          </div>
          {loading ? (
            <div className="h-36 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ) : recent.length === 0 ? (
            <Card><CardContent className="py-8 text-center">
              <Images className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p className="text-xs text-gray-500">No recent uploads yet — uploaded photos and highlights will appear here.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="recent-uploads-grid">
              {recent.map(item => {
                const thumb = absoluteUrl(item.thumbnail_url);
                const isHighlight = item.item_type === 'highlight';
                const isLink = item.media_type === 'link';
                return (
                  <div
                    key={`${item.item_type}-${item.id}`}
                    onClick={() => navigate(`/athletes/${item.athlete_id}/media`)}
                    className="group cursor-pointer rounded-lg border bg-white dark:bg-gray-900 overflow-hidden hover:shadow-md hover:border-emerald-400 transition-all"
                    data-testid={`recent-item-${item.id}`}
                  >
                    <div className="relative aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={item.caption || 'media'}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className={`absolute inset-0 ${thumb ? 'hidden' : 'flex'} items-center justify-center text-gray-400`}>
                        {isHighlight ? <Film className="w-7 h-7" /> : isLink ? <Link2 className="w-7 h-7" /> : <ImageIcon className="w-7 h-7" />}
                      </div>
                      <div className="absolute top-1.5 left-1.5 flex gap-1">
                        <Badge className={`text-[10px] px-1.5 py-0 ${isHighlight ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                          {item.label}
                        </Badge>
                        {isLink && (
                          <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-blue-200 flex items-center gap-0.5">
                            <Link2 className="w-2.5 h-2.5" /> Link
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate" title={item.athlete_name}>{item.athlete_name}</p>
                      <div className="flex items-center justify-between text-[10px] text-gray-400 mt-0.5">
                        <span className="truncate pr-1">{item.caption || '—'}</span>
                        <span className="flex-shrink-0">{fmtDate(item.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ====== ATHLETE PICKER ====== */}
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">All Athletes</h3>
        </div>
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search athletes by name or squad…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-9 h-9"
            data-testid="media-athlete-search"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <Images className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 text-sm">No athletes found</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="media-athlete-grid">
            {filtered.map(a => (
              <Card
                key={a.id}
                onClick={() => navigate(`/athletes/${a.id}/media`)}
                className="hover:shadow-md hover:border-emerald-400 transition-all cursor-pointer"
                data-testid={`media-athlete-card-${a.id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-700 dark:text-emerald-300 font-semibold flex-shrink-0">
                    {(a.full_name || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{a.full_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {a.squad && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{a.squad}</Badge>}
                      {a.position && <span className="text-[10px] text-gray-400">{a.position}</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MediaLanding;
